/**
 * Product Bundle Options Handler
 * Handles the display and functionality of optional product bundles
 */
class ProductBundleOptions {
  constructor() {
    this.bundleContainer = document.querySelector('.product-bundle-options');
    this.checkboxes = document.querySelectorAll('.product-bundle-option__checkbox');
    this.includedOptions = document.querySelectorAll('.product-bundle-option--included');
    this.selectedList = document.querySelector('.product-bundle-options__selected-list');
    this.totalPrice = document.querySelector('.product-bundle-options__total-price');
    this.originalPrice = null;
    this.specialOfferSection = document.querySelector('.product-bundle-special-offer');
    this.freeAccessoryRadios = document.querySelectorAll('.special-offer-option__radio');
    this.bundleDiscount = this.bundleContainer ? parseInt(this.bundleContainer.dataset.bundleDiscount) || 0 : 0;
    this.isValetonFreeBag = this.bundleContainer && this.bundleContainer.dataset.valetonFreeBag === 'true';
    this.variantChangeUnsubscriber = null;
    
    if (this.bundleContainer) {
      this.init();
    }
  }

  init() {
    // Store original price
    if (this.totalPrice) {
      this.originalPrice = this.totalPrice.textContent;
    }

    // Valeton free bag: show only the included bag for current variant, listen for variant change
    if (this.isValetonFreeBag && this.includedOptions.length) {
      this.updateValetonIncludedVisibility();
      const PUB_SUB = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
      if (typeof subscribe !== 'undefined' && PUB_SUB) {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB.variantChange, (event) => {
          const sectionId = this.bundleContainer?.dataset.sectionId;
          if (event?.data?.sectionId !== sectionId) return;
          this.updateValetonIncludedVisibility();
          this.updateSummary();
        });
      }
      // Fallback: listen for change on variant hidden input (e.g. theme doesn't use pub/sub)
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && !this.variantChangeUnsubscriber) {
        variantInput.addEventListener('change', () => {
          this.updateValetonIncludedVisibility();
          this.updateSummary();
        });
      }
    }

    // Add event listeners to checkboxes
    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.updateSummary();
        this.checkSpecialOffer();
      });
    });

    // Add event listeners to free accessory radios
    this.freeAccessoryRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.updateSummary();
      });
    });

    // Initial summary update
    this.updateSummary();
    this.checkSpecialOffer();

    const refreshSummary = (e) => {
      const t = e && e.target;
      if (t && t.matches && t.matches('.quantity__input')) {
        this.updateSummary();
      }
    };
    document.addEventListener('change', refreshSummary);
    document.addEventListener('input', refreshSummary);
    document.addEventListener('click', (e) => {
      if (e.target && e.target.closest && e.target.closest('.quantity__button')) {
        setTimeout(() => this.updateSummary(), 0);
      }
    });
  }

  updateValetonIncludedVisibility() {
    if (!this.includedOptions.length) return;
    const variantInput = document.querySelector('input[name="id"]');
    const currentVariantId = variantInput ? variantInput.value.trim() : '';
    this.includedOptions.forEach(option => {
      const mainIds = (option.dataset.mainVariantIds || '').split(',').map(id => id.trim()).filter(Boolean);
      const isForCurrentVariant = mainIds.includes(currentVariantId);
      option.style.display = isForCurrentVariant ? '' : 'none';
      option.classList.toggle('product-bundle-option--active', isForCurrentVariant);
    });
  }

  updateSummary() {
    const selectedItems = [];
    let totalPrice = 0;

    // Get main product price (assuming it's displayed somewhere on the page)
    const mainProductPrice = this.getMainProductPrice();
    totalPrice += mainProductPrice;

    // Valeton free bag: add the visible included bag to selected items (auto-included when in stock)
    if (this.isValetonFreeBag && this.includedOptions.length) {
      const visibleIncluded = Array.from(this.includedOptions).find(opt => opt.style.display !== 'none');
      if (visibleIncluded && visibleIncluded.dataset.productTitle) {
        const available = visibleIncluded.dataset.bagAvailable !== 'false';
        const suffix = available ? ' (Included)' : ' (Included – temporarily out of stock)';
        selectedItems.push(visibleIncluded.dataset.productTitle + suffix);
      }
    }

    // Calculate selected bundle items
    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const productTitle = checkbox.dataset.productTitle;
        const productPrice = this.parsePrice(checkbox.dataset.productPrice);
        
        selectedItems.push(productTitle);
        totalPrice += productPrice;
      }
    });

    // Add free accessory price (if selected)
    this.freeAccessoryRadios.forEach(radio => {
      if (radio.checked) {
        const productTitle = radio.dataset.productTitle;
        const productPrice = this.parsePrice(radio.dataset.productPrice);
        
        selectedItems.push(productTitle + ' (FREE)');
        totalPrice += productPrice; // Add the original price, discount will be applied below
      }
    });

    // Apply bundle discount if all add-ons are selected
    const totalCheckboxes = this.checkboxes.length;
    const checkedCheckboxes = Array.from(this.checkboxes).filter(cb => cb.checked).length;
    const hasFreeAccessory = Array.from(this.freeAccessoryRadios).some(radio => radio.checked);
    
    if (totalCheckboxes > 0 && checkedCheckboxes === totalCheckboxes && this.bundleDiscount > 0) {
      if (hasFreeAccessory) {
        // When ALL add-ons + free accessory are selected: -(RM50+RM99) or -(RM50+RM98)
        totalPrice -= (50 + this.bundleDiscount);
      } else {
        // When ALL add-ons are selected (without free accessory): -RM99 or -RM98
        totalPrice -= this.bundleDiscount;
      }
    }

    // Update selected items display
    if (this.selectedList) {
      if (selectedItems.length > 0) {
        this.selectedList.textContent = selectedItems.join(', ');
      } else {
        this.selectedList.textContent = 'None selected';
      }
    }

    // Update total price
    if (this.totalPrice) {
      this.totalPrice.textContent = this.formatPrice(totalPrice);
    }
  }

  getMainProductPrice() {
    // Prefer the live sale/dealer price so the bundle total matches the product page
    const priceElement = document.querySelector('.price--large .price-item--sale') ||
                       document.querySelector('.price__dealer-discount .price-item--sale') ||
                       document.querySelector('.price__regular .price-item--regular') || 
                       document.querySelector('.price .price-item--regular') ||
                       document.querySelector('[data-price]') ||
                       document.querySelector('.price');
    
    if (priceElement) {
      const priceText = priceElement.textContent;
      //console.log('Main product price text:', priceText); // Debug log
      const parsedPrice = this.parsePrice(priceText);
      //console.log('Parsed main product price:', parsedPrice); // Debug log
      return parsedPrice;
    }
    
    // Fallback: try to get from the original price we stored
    if (this.originalPrice) {
      //console.log('Using original price:', this.originalPrice); // Debug log
      return this.parsePrice(this.originalPrice);
    }
    
    //console.log('No main product price found, returning 0'); // Debug log
    return 0;
  }

  parsePrice(priceString) {
    // Remove currency symbols and parse the number
    // Handle Malaysian Ringgit format: RM10,499.00
    let cleanPrice = priceString.replace(/[^\d.,]/g, '');
    
    // If there's a comma followed by exactly 2 digits, it's likely a decimal separator
    // Otherwise, commas are thousands separators
    if (cleanPrice.includes(',') && cleanPrice.includes('.')) {
      // Format: 10,499.00 - comma is thousands separator
      cleanPrice = cleanPrice.replace(/,/g, '');
    } else if (cleanPrice.includes(',') && !cleanPrice.includes('.')) {
      // Check if comma is followed by exactly 2 digits (decimal)
      const parts = cleanPrice.split(',');
      if (parts.length === 2 && parts[1].length === 2) {
        // Format: 10499,00 - comma is decimal separator
        cleanPrice = cleanPrice.replace(',', '.');
      } else {
        // Format: 10,499 - comma is thousands separator
        cleanPrice = cleanPrice.replace(/,/g, '');
      }
    }
    
    const price = parseFloat(cleanPrice);
    return isNaN(price) ? 0 : price;
  }

  formatPrice(price) {
    // Format price back to currency format with MYR
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(price);
  }

  checkSpecialOffer() {
    if (!this.specialOfferSection) return;
    
    // Count total checkboxes and checked checkboxes
    const totalCheckboxes = this.checkboxes.length;
    const checkedCheckboxes = Array.from(this.checkboxes).filter(cb => cb.checked).length;
    
    // Show special offer if ALL add-on items are selected
    if (totalCheckboxes > 0 && checkedCheckboxes === totalCheckboxes) {
      this.specialOfferSection.style.display = 'block';
    } else {
      this.specialOfferSection.style.display = 'none';
      // Uncheck any selected free accessories
      this.freeAccessoryRadios.forEach(radio => {
        radio.checked = false;
      });
    }
  }

  getSelectedProducts() {
    const selectedProducts = [];
    
    // Add regular bundle items
    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        selectedProducts.push({
          id: checkbox.value,
          title: checkbox.dataset.productTitle,
          price: checkbox.dataset.productPrice
        });
      }
    });
    
    // Add free accessory if selected
    this.freeAccessoryRadios.forEach(radio => {
      if (radio.checked) {
        selectedProducts.push({
          id: radio.value,
          title: radio.dataset.productTitle,
          price: 'FREE',
          isFree: true
        });
      }
    });
    
    return selectedProducts;
  }
}

/**
 * Enhanced Product Form for Bundle Support
 * Extends the existing product form to handle bundle additions
 */
class BundleProductForm extends HTMLElement {
  constructor() {
    super();
    this.bundleOptions = new ProductBundleOptions();
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    
    if (this.form && this.submitButton) {
      this.init();
    }
  }

  init() {
    // Override the form submission
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    // Show loading state
    this.setLoadingState(true);

    try {
      // Get main product data
      const mainProductId = this.form.querySelector('input[name="id"]').value;
      const quantity = this.form.querySelector('input[name="quantity"]')?.value || 1;

      // Build items array for cart
      const itemsToAdd = [
        {
          id: mainProductId,
          quantity: parseInt(quantity)
        }
      ];

      // Auto-add Valeton GP-200 free bag for non-dealer (variant id -> bag variant id map from Liquid)
      const freeBagMapEl = document.getElementById('valeton-free-bag-map');
      if (freeBagMapEl && freeBagMapEl.textContent) {
        try {
          const freeBagMap = JSON.parse(freeBagMapEl.textContent);
          const bagVariantId = freeBagMap[mainProductId];
          if (bagVariantId) {
            itemsToAdd.push({
              id: bagVariantId,
              quantity: 1,
              properties: {
                '_free_item': 'true',
                '_valeton_gp200_bag': 'true'
              }
            });
          }
        } catch (e) {
          console.warn('Valeton free bag map parse error:', e);
        }
      }

      // Add selected bundle items
      const selectedProducts = this.bundleOptions.getSelectedProducts();
      selectedProducts.forEach(product => {
        if (product.isFree) {
          // For free items, add with quantity 1 but note it's free
          itemsToAdd.push({
            id: product.id,
            quantity: 1,
            properties: {
              '_free_item': 'true',
              '_bundle_offer': 'px-electric-guitar'
            }
          });
        } else {
          itemsToAdd.push({
            id: product.id,
            quantity: 1
          });
        }
      });

      // Add to cart using Shopify's AJAX API
      const response = await this.addMultipleItemsToCart(itemsToAdd);
      
      if (response.status) {
        // Handle errors
        this.handleError(response.description || response.message);
        return;
      }

      // Success - update cart or redirect
      if (this.cart && typeof this.cart.renderContents === 'function') {
        try {
          this.updateCart(response);
        } catch (error) {
          console.warn('Cart update failed, redirecting to cart page:', error);
          window.location.href = '/cart';
        }
      } else {
        window.location.href = '/cart';
      }

    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.handleError('There was an error adding items to your cart. Please try again.');
    } finally {
      this.setLoadingState(false);
    }
  }

  async addMultipleItemsToCart(items) {
    //console.log('Adding items to cart:', items); // Debug log
    
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    };

    // Only add sections if cart drawer exists and has the method
    if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
      try {
        const sections = this.cart.getSectionsToRender().map(section => section.id);
        config.body = JSON.stringify({ 
          items,
          sections,
          sections_url: window.location.pathname
        });
      } catch (error) {
        console.warn('Error getting sections for cart update:', error);
        // Continue without sections
      }
    }

    //console.log('Cart request config:', config); // Debug log
    
    const response = await fetch('/cart/add.js', config);
    const result = await response.json();
    
    //console.log('Cart response:', result); // Debug log
    
    return result;
  }

  updateCart(cartData) {
    try {
      if (!this.cart || typeof this.cart.renderContents !== 'function') {
        window.location.href = '/cart';
        return;
      }
      // Multi-item add returns { items: [...] }; cart notification expects { key, sections }
      let payload = cartData;
      if (cartData.items && Array.isArray(cartData.items) && cartData.items.length > 0) {
        const lastItem = cartData.items[cartData.items.length - 1];
        payload = {
          key: lastItem.key,
          sections: cartData.sections || {}
        };
        const sectionIds = this.cart.getSectionsToRender().map(s => s.id);
        const hasSections = sectionIds.every(id => payload.sections[id]);
        if (!hasSections) {
          window.location.href = '/cart';
          return;
        }
      }
      this.cart.renderContents(payload);
    } catch (error) {
      //console.error('Error updating cart:', error);
      window.location.href = '/cart';
    }
  }

  setLoadingState(loading) {
    if (loading) {
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      const spinner = this.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
      
      if (this.bundleOptions.bundleContainer) {
        this.bundleOptions.bundleContainer.classList.add('loading');
      }
    } else {
      this.submitButton.removeAttribute('aria-disabled');
      this.submitButton.classList.remove('loading');
      const spinner = this.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
      
      if (this.bundleOptions.bundleContainer) {
        this.bundleOptions.bundleContainer.classList.remove('loading');
      }
    }
  }

  handleError(message) {
    // Show error message (you can customize this based on your theme's error handling)
    console.error('Cart error:', message);
    
    // Try to use existing error handling from the theme
    const errorWrapper = this.querySelector('.product-form__error-message-wrapper');
    if (errorWrapper) {
      const errorMessage = errorWrapper.querySelector('.product-form__error-message');
      if (errorMessage) {
        errorMessage.textContent = message;
        errorWrapper.removeAttribute('hidden');
      }
    } else {
      // Fallback: show alert
      alert(message);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Register the custom element
  if (!customElements.get('bundle-product-form')) {
    customElements.define('bundle-product-form', BundleProductForm);
  }
  
  // Initialize bundle options
  new ProductBundleOptions();
});
